import React, { useState } from 'react';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios.js';
import PropTypes from 'prop-types';

// material-ui
import { Box, Grid } from '@mui/material';

// project import
import AuthCard from './AuthCard';
import AuthFooter from 'components/cards/AuthFooter';

// assets
import AuthBackground from 'assets/images/auth/AuthBackground';

// material-ui
import {
  Button,
  FormHelperText,

  Link,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import LockOutlined from '@mui/icons-material/LockOutlined';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

import AnimateButton from 'components/@extended/AnimateButton';
import { completeLogin } from 'utils/completeLogin';

// assets
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

// ==============================|| MPIN VERIFICATION ||============================== //

const MpinVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const MPINToken = location.state && location.state.MPINToken;
  const MaskedMobileNumber = location.state && location.state.MaskedMobileNumber;

  const [showMpin, setShowMpin] = useState(false);
  const [redirect, setRedirect] = useState(false);

  // Reached this page directly (refresh, back button, bookmarked URL, etc.)
  // without a valid challenge from /login - nothing to verify, send them back.
  if (!MPINToken) {
    return <Navigate to="/login" replace />;
  }

  if (redirect) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
    <Box sx={{ minHeight: '100vh' }}>
        <AuthBackground />
        <Grid
          container
          direction="column"
          justifyContent="flex-end"
          sx={{
            minHeight: '100vh'
          }}
        >
          <Grid item xs={12}>
            <Grid
              item
              xs={12}
              container
              justifyContent="center"
              alignItems="center"
              sx={{ minHeight: { xs: 'calc(100vh - 134px)', md: 'calc(100vh - 112px)' } }}
            >
              <Grid item>
              <AuthCard>
                <Stack spacing={1} sx={{ mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                    <LockOutlined color="primary" />
                    <Typography variant="h5">Enter your MPIN</Typography>
                    </Stack>
                    <Typography color="text.secondary">
                    {MaskedMobileNumber
                        ? `Two-factor authentication is enabled for this account. Enter the 4-digit MPIN for the account registered to ${MaskedMobileNumber}.`
                        : 'Two-factor authentication is enabled for this account. Enter your 4-digit MPIN to continue.'}
                    </Typography>
                </Stack>

                <Formik
                    initialValues={{ MPIN: '', submit: null }}
                    validationSchema={Yup.object().shape({
                    MPIN: Yup.string()
                        .matches(/^\d{4}$/, 'MPIN must be exactly 4 digits')
                        .required('MPIN is required')
                    })}
                    onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                    try {
                        const response = await axios.post('/verify-mpin', {
                        MPINToken,
                        MPIN: values.MPIN
                        });

                        if (response.data.Status === 0) {
                        setStatus({ success: false });
                        setErrors({ submit: response.data.Message });
                        setSubmitting(false);
                        } else {
                        setStatus({ success: true });
                        completeLogin(response.data);
                        setRedirect(true);
                        }
                    } catch (err) {
                        setStatus({ success: false });
                        setErrors({ submit: err.message });
                        setSubmitting(false);
                    }
                    }}
                >
                    {({ errors, handleBlur, handleSubmit, isSubmitting, touched, values, setFieldValue }) => (
                    <form noValidate onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Stack spacing={1}>
                            <InputLabel htmlFor="mpin-verify">MPIN</InputLabel>
                            <OutlinedInput
                                id="mpin-verify"
                                name="MPIN"
                                type={showMpin ? 'text' : 'password'}
                                inputMode="numeric"
                                autoFocus
                                value={values.MPIN}
                                onBlur={handleBlur}
                                onChange={(e) => setFieldValue('MPIN', e.target.value.replace(/\D/g, '').slice(0, 4))}
                                placeholder="4 digit PIN"
                                fullWidth
                                error={Boolean(touched.MPIN && errors.MPIN)}
                                endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                    aria-label={showMpin ? 'Hide MPIN' : 'Show MPIN'}
                                    onClick={() => setShowMpin((prev) => !prev)}
                                    edge="end"
                                    size="large"
                                    >
                                    {showMpin ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                    </IconButton>
                                </InputAdornment>
                                }
                            />
                            {touched.MPIN && errors.MPIN && (
                                <FormHelperText error id="mpin-verify-error">
                                {errors.MPIN}
                                </FormHelperText>
                            )}
                            </Stack>
                        </Grid>

                        {errors.submit && (
                            <Grid item xs={12}>
                            <FormHelperText error>{errors.submit}</FormHelperText>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <AnimateButton>
                            <Button disableElevation disabled={isSubmitting} fullWidth size="large" type="submit" variant="contained" color="primary">
                                Verify
                            </Button>
                            </AnimateButton>
                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                            <Link
                                variant="h6"
                                component={RouterLink}
                                to="/reset-mpin"
                                state={{ MPINToken, MaskedMobileNumber }}
                                color="text.primary"
                            >
                                Forgot MPIN?
                            </Link>
                            <Link variant="h6" component={RouterLink} to="/login" color="text.primary">
                                Use a different account
                            </Link>
                            </Stack>
                        </Grid>
                        </Grid>
                    </form>
                    )}
                </Formik>
            </AuthCard>
      </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ m: 3, mt: 1 }}>
        <AuthFooter />
      </Grid>
    </Grid>
  </Box>
    </>
  );
};

export default MpinVerification;
